/**
 * Uma face da oclusão na revisão.
 *
 * Com marcação perguntada, `aparenciaDaMarcacao` decide o papel de cada caixa
 * (neutra, perguntada coberta/revelada, rotulada) e `textoDaMarcacao` o que
 * ela mostra — a mesma dupla que a pré-visualização do editor lê (design,
 * Decisão 3). Revelar na frente mostra o rótulo da perguntada e chama
 * `onRevelar` — o verso (as quatro notas) abre sem virar o cartão e sem
 * registrar nota; por isso o papel é calculado a partir de `faceEfetiva`, não
 * `face`, para que esse peek na frente já renderize como revelada. Sem
 * marcação perguntada, cada toque alterna só aquela caixa.
 *
 * O bloco de prompt (sobrancelha, texto principal, explicação) lê a mesma
 * `faceEfetiva` pela mesma razão: o peek na frente já mostra o rótulo
 * revelado na imagem, e o texto abaixo dela não pode continuar perguntando.
 *
 * A imagem é `frontImageKey`, lida com `useAuthedImage`. `resolveImageUrl` e
 * `faseDaImagem` (stories) pulam essa leitura. As marcações só entram depois
 * que a `<img>` tem caixa pintada, em % dessa caixa (`getBoundingClientRect`),
 * nunca da moldura — uma moldura pode ser mais larga que a imagem.
 */
import { useLayoutEffect, useRef, useState } from 'react';
import type { MouseEvent, PointerEvent } from 'react';
import type { OcclusionStudyMode, ReviewMask } from '../../../sdk/@types/Revisao';
import useAuthedImage from '../../../core/hooks/useAuthedImage';

import CardMarkdown from './CardMarkdown';
import {
  aparenciaDaMarcacao,
  classeDaForma,
  textoDaMarcacao,
  CLASSES_DA_APARENCIA,
  GRADIENTE_PERGUNTADA,
} from '../OcclusionEditor/marcacaoAparencia';
import type { AparenciaMarcacao } from '../OcclusionEditor/marcacaoAparencia';
import { revisaoPrimaryActionClass } from './revisaoSurfaces';

export interface OclusaoFaceProps {
  masks: readonly ReviewMask[];
  /** Stored file key. Read with useAuthedImage unless resolveImageUrl or faseDaImagem is set. */
  frontImageKey?: string;
  /** Story override. A sync URL. Skips the authenticated read. */
  resolveImageUrl?: (fileKey: string) => string;
  /**
   * Story override for the three image phases. When set, no fetch runs.
   * 'pronta' still paints resolveImageUrl(frontImageKey).
   * The app omits this.
   */
  faseDaImagem?: 'carregando' | 'pronta' | 'falha';
  face: 'front' | 'back';
  mode: OcclusionStudyMode; // 'ocultar-todas' | 'ocultar-uma'
  /** null: the read did not name a marking. */
  askedIndex: number | null;
  /** The card's `back` field. Absent, null, or whitespace-only counts as no explanation — no reserved line. */
  explicacao?: string | null;
  /** Go to the verso. Never a rating. */
  onRevelar?: () => void;
}

type FaseImagem = 'carregando' | 'pronta' | 'falha' | 'ausente';

interface CaixaDaImagem {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface MedidaDaImagem {
  src: string;
  caixa: CaixaDaImagem;
}

const CAIXA =
  'absolute flex items-center justify-center border-2 px-1 text-center text-xs font-semibold md:text-sm';

const SOBRANCELHA_FRENTE = 'IDENTIFIQUE A ESTRUTURA';

/** Eyebrow text. Derived from the effective face and the asked marking's number — spec: never a field the author fills. */
function sobrancelhaDoPrompt(faceEfetiva: 'front' | 'back', numero: number): string {
  return faceEfetiva === 'front' ? SOBRANCELHA_FRENTE : `RESPOSTA · MARCAÇÃO ${numero}`;
}

/** Main text: the question on the front, referring to the marking by number — never by colour; the revealed label on the back. */
function textoPrincipalDoPrompt(faceEfetiva: 'front' | 'back', numero: number, rotulo: string): string {
  return faceEfetiva === 'front' ? `O que está escondido na marcação ${numero}?` : rotulo;
}

/** The card's `back` field. Absent, null, or whitespace-only is no explanation — never an empty reserved line. */
function explicacaoValida(explicacao: string | null | undefined): string | null {
  const texto = explicacao?.trim();
  return texto ? texto : null;
}

/** Identifica a unidade montada, para a próxima marcação começar coberta. */
function chaveDaUnidade(
  masks: readonly ReviewMask[],
  askedIndex: number | null,
  mode: OcclusionStudyMode,
): string {
  const marcas = masks
    .map((mask) => `${mask.id ?? ''}:${mask.label}:${mask.x}:${mask.y}:${mask.w}:${mask.h}`)
    .join('|');
  return `${mode}#${askedIndex ?? ''}#${marcas}`;
}

/**
 * `loading` do hook começa falso. Com chave e sem object URL, a primeira
 * pintura — antes do efeito — já é `carregando`. Sem chave é `ausente`:
 * espaço reservado, sem frase de erro e sem marcações.
 */
function faseDaLeitura(
  faseDaImagem: OclusaoFaceProps['faseDaImagem'],
  frontImageKey: string | undefined,
  resolveImageUrl: OclusaoFaceProps['resolveImageUrl'],
  url: string | null,
  error: boolean,
): FaseImagem {
  if (faseDaImagem) return faseDaImagem;
  if (resolveImageUrl && frontImageKey) return 'pronta';
  if (frontImageKey && error) return 'falha';
  if (frontImageKey && !url) return 'carregando';
  if (!frontImageKey) return 'ausente';
  return 'pronta';
}

/** Só o object URL do hook, ou `resolveImageUrl(frontImageKey)`. Nunca um path de `/files`. */
function enderecoPronto(
  fase: FaseImagem,
  frontImageKey: string | undefined,
  resolveImageUrl: OclusaoFaceProps['resolveImageUrl'],
  url: string | null,
): string | null {
  if (fase !== 'pronta') return null;
  if (resolveImageUrl && frontImageKey) return resolveImageUrl(frontImageKey);
  return url;
}

function OclusaoFace({
  masks,
  frontImageKey,
  resolveImageUrl,
  faseDaImagem,
  face,
  mode,
  askedIndex,
  explicacao,
  onRevelar,
}: OclusaoFaceProps) {
  const pularLeitura = faseDaImagem !== undefined || resolveImageUrl !== undefined;
  const { url, error } = useAuthedImage(pularLeitura ? undefined : frontImageKey);
  const fase = faseDaLeitura(faseDaImagem, frontImageKey, resolveImageUrl, url, error);
  const src = enderecoPronto(fase, frontImageKey, resolveImageUrl, url);

  const molduraRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [medida, setMedida] = useState<MedidaDaImagem | null>(null);
  const caixa = medida !== null && medida.src === src ? medida.caixa : null;

  const medirRef = useRef<() => void>(() => {});
  medirRef.current = () => {
    const imagem = imgRef.current;
    const moldura = molduraRef.current;
    if (!imagem || !moldura || !src) {
      setMedida(null);
      return;
    }
    const imgRect = imagem.getBoundingClientRect();
    if (imgRect.width === 0 || imgRect.height === 0) {
      setMedida(null);
      return;
    }
    const frameRect = moldura.getBoundingClientRect();
    const next: CaixaDaImagem = {
      left: imgRect.left - frameRect.left,
      top: imgRect.top - frameRect.top,
      width: imgRect.width,
      height: imgRect.height,
    };
    setMedida((atual) => {
      if (
        atual &&
        atual.src === src &&
        atual.caixa.left === next.left &&
        atual.caixa.top === next.top &&
        atual.caixa.width === next.width &&
        atual.caixa.height === next.height
      ) {
        return atual;
      }
      return { src, caixa: next };
    });
  };

  useLayoutEffect(() => {
    if (!src) return;
    const imagem = imgRef.current;
    if (!imagem) return;
    const run = () => medirRef.current();
    if (imagem.complete) run();
    const observer = new ResizeObserver(run);
    observer.observe(imagem);
    window.addEventListener('resize', run);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', run);
    };
  }, [src]);

  const [revelada, setRevelada] = useState(false);
  const [abertas, setAbertas] = useState<ReadonlySet<number>>(() => new Set());
  const revelouNoPonteiro = useRef(false);
  const unidade = chaveDaUnidade(masks, askedIndex, mode);
  const [unidadeVista, setUnidadeVista] = useState(unidade);
  if (unidade !== unidadeVista) {
    setUnidadeVista(unidade);
    setRevelada(false);
    setAbertas(new Set());
    revelouNoPonteiro.current = false;
  }

  const revelar = () => {
    if (face !== 'front' || askedIndex === null) return;
    setRevelada(true);
    onRevelar?.();
  };

  const alternar = (index: number) => {
    setAbertas((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(index)) proximo.delete(index);
      else proximo.add(index);
      return proximo;
    });
  };

  /** Front, peeked open before the actual flip: renders like the back (design, Decisão 3). */
  const faceEfetiva: 'front' | 'back' = face === 'front' && revelada ? 'back' : face;

  /** The one place that decides a mask's role. Text and class read it below — never face/mode/askedIndex again. */
  const papelDaMarcacao = (index: number): AparenciaMarcacao => {
    if (askedIndex === null) return abertas.has(index) ? 'rotulada' : 'neutra';
    return aparenciaDaMarcacao(faceEfetiva, mode, index, askedIndex, 'estudo');
  };

  const aoPressionar = (event: PointerEvent<HTMLDivElement>, index: number) => {
    event.stopPropagation();
    if (askedIndex === null || face !== 'front' || index !== askedIndex) return;
    revelouNoPonteiro.current = true;
    revelar();
  };

  const aoClicar = (event: MouseEvent<HTMLDivElement>, index: number) => {
    event.stopPropagation();
    if (askedIndex === null) {
      alternar(index);
      return;
    }
    const veioDoPonteiro = revelouNoPonteiro.current;
    revelouNoPonteiro.current = false;
    if (veioDoPonteiro || face !== 'front' || index !== askedIndex) return;
    revelar();
  };

  const explicacaoTexto = explicacaoValida(explicacao);
  const prompt =
    askedIndex !== null
      ? {
          sobrancelha: sobrancelhaDoPrompt(faceEfetiva, askedIndex + 1),
          principal: textoPrincipalDoPrompt(faceEfetiva, askedIndex + 1, masks[askedIndex]?.label ?? ''),
          explicacao: faceEfetiva === 'back' ? explicacaoTexto : null,
        }
      : null;

  const marcacoes =
    caixa === null
      ? null
      : masks.map((mask, index) => {
          const papel = papelDaMarcacao(index);
          const aberta = papel === 'perguntada-revelada' || papel === 'rotulada';
          const destacada = papel === 'perguntada-coberta' || papel === 'perguntada-revelada';
          return (
            <div
              key={mask.id ?? index}
              data-mask
              data-mask-index={index}
              data-mask-estado={aberta ? 'revelada' : 'coberta'}
              data-mask-papel={papel}
              {...(destacada ? { 'data-asked': 'true' } : {})}
              onPointerDown={(event) => aoPressionar(event, index)}
              onClick={(event) => aoClicar(event, index)}
              className={`pointer-events-auto ${CAIXA} ${classeDaForma(mask.shape)} ${CLASSES_DA_APARENCIA[papel]}`}
              style={{
                left: `${mask.x}%`,
                top: `${mask.y}%`,
                width: `${mask.w}%`,
                height: `${mask.h}%`,
                ...(papel === 'perguntada-coberta' ? { background: GRADIENTE_PERGUNTADA } : {}),
              }}
            >
              <span className="max-h-full overflow-hidden break-words leading-tight">
                {textoDaMarcacao(papel, index, mask.label)}
              </span>
            </div>
          );
        });

  return (
    <div
      data-oclusao-face={face}
      data-oclusao-modo={mode}
      className="flex w-full flex-col items-center gap-3"
    >
      {fase === 'pronta' && src ? (
        <div
          ref={molduraRef}
          data-oclusao-imagem="pronta"
          className="relative inline-block max-w-full"
        >
          <img
            ref={imgRef}
            src={src}
            alt="Cartão de oclusão"
            draggable={false}
            className="block max-h-[60vh] max-w-full rounded-card"
            onLoad={() => medirRef.current()}
          />
          {caixa ? (
            <div
              className="pointer-events-none absolute"
              style={{
                left: caixa.left,
                top: caixa.top,
                width: caixa.width,
                height: caixa.height,
              }}
            >
              {marcacoes}
            </div>
          ) : null}
        </div>
      ) : fase === 'falha' ? (
        <div
          data-oclusao-imagem="falha"
          className="flex min-h-[12rem] w-full items-center justify-center rounded-card bg-surface-muted px-4 text-center"
        >
          <p className="text-sm text-danger-ink">Não foi possível carregar a imagem.</p>
        </div>
      ) : fase === 'carregando' ? (
        <div
          data-oclusao-imagem="carregando"
          aria-busy="true"
          className="min-h-[12rem] w-full rounded-card bg-surface-muted"
        />
      ) : (
        <div className="min-h-[12rem] w-full rounded-card bg-surface-muted" />
      )}

      {prompt ? (
        <div
          data-prompt
          className="flex w-full flex-col items-start gap-1.5 text-left md:items-center md:text-center"
        >
          <span
            data-prompt-eyebrow
            className={`text-xs font-semibold uppercase tracking-wider ${
              faceEfetiva === 'front'
                ? 'text-ask-to dark:text-ask-from'
                : 'text-reveal dark:text-reveal-ring'
            }`}
          >
            {prompt.sobrancelha}
          </span>
          <p
            data-prompt-principal
            className={`w-full break-words leading-snug text-ink ${
              faceEfetiva === 'front' ? 'text-xl font-semibold md:text-2xl' : 'text-2xl font-bold md:text-3xl'
            }`}
          >
            {prompt.principal}
          </p>
          {prompt.explicacao !== null ? (
            <div data-prompt-explicacao className="w-full">
              <CardMarkdown
                value={prompt.explicacao}
                className="text-left text-sm text-ink-secondary md:text-center"
                resolveImageUrl={resolveImageUrl}
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {face === 'front' && askedIndex !== null ? (
        <button
          type="button"
          data-revelar=""
          className={`${revisaoPrimaryActionClass} w-full sm:w-auto`}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            revelar();
          }}
        >
          Revelar
        </button>
      ) : null}
    </div>
  );
}

export { OclusaoFace };
export default OclusaoFace;
